import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { ThemeService } from './services/theme.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Meta, Title } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersService } from './services/users.service';
import { PwaService } from './services/pwa.service';
import { filter } from 'rxjs';
import { LoggerService } from './services/logger.service';
import { ScrollPositionService } from './services/scroll-position.service';
import { environment } from '../environments/environment';

declare let gtag: Function;

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {

  pageTitle = 'Strona startowa';
  pageIntro = 'Program osłonowy ZGrane Grono powstał z myślą o wzmacnianiu lokalnej wspólnoty i budowaniu więzi między mieszkańcami Zielonej Góry. Jego głównym celem jest wspieranie różnych grup społecznych poprzez umożliwienie im korzystania z atrakcyjnych zniżek, ofert i wydarzeń, które sprzyjają aktywnemu uczestnictwu w życiu miasta.';
  isLoggedIn: boolean = false;
  hasAdminSession: boolean = false;
  showGoTop = false;
  showGoBack = false;
  showInstallPwa = false;
  fullWidthPage = false;


  get appVersion(): string {
    return environment.version;
  }

  get buildNumber(): string {
    return this.loggerService.buildNumber;
  }
  get remoteVersionNumber(): string {
    return this.loggerService.appVersion;
  }
  private wasOffline = false;
  private connectivityCheckInterval: any = null;


  get isAdminRoute(): boolean {
    const url = this.router.url;
    return typeof url === 'string' && url.startsWith('/admin');
  }

  get isSupportRoute(): boolean {
    const url = this.router.url;
    return typeof url === 'string' && url.startsWith('/support');
  }

  isMobile = false;
  isNarrow = false;
  mobileFooterExpanded = false;
  wcagMode = false;

  constructor(private router: Router,
    private location: Location,
    public themeService: ThemeService,
    private userService: UsersService,
    private breakpointObserver: BreakpointObserver,
    private titleService: Title,
    private snackBar: MatSnackBar,
    private metaService: Meta,
    private pwaService: PwaService,
    private loggerService: LoggerService,
    private scrollPositionService: ScrollPositionService) {

      this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        gtag('config', 'G-Z2ZRH8D5SC', {
          page_path: event.urlAfterRedirects
        });
      });


    this.breakpointObserver
      .observe(['(max-width: 1125px)'])
      .subscribe((result: any) => {
        this.isMobile = result.matches || this.pwaService.isPwaInstalled();
        this.pwaService.setMobileRunning(this.isMobile);
        if (!this.isMobile) {
          this.mobileFooterExpanded = false;
        }
      });

    this.breakpointObserver
      .observe(['(max-width: 460px)'])
      .subscribe((result: any) => {
        this.isNarrow = result.matches;
      });

    window.addEventListener('appinstalled', () => {
      this.isMobile = true;
      this.pwaService.setMobileRunning(true);
    });

  }

  menuItems = [
   // { label: 'Aktualności', link: '/wiadomosci' },
    { label: 'Oferty', link: '/oferty' },
    { label: 'Partnerzy', link: '/partnerzy' },
    { label: 'O ZGranym Gronie', link: '/informacje_o_karcie' },
    { label: 'Zostań partnerem', link: '/zostan-naszym-partnerem' },
  ];

  get maintenanceMode(): boolean {
    return this.loggerService.maintenanceMode;
  }

  get maintenancePlanning(): boolean {
    return this.loggerService.maintenancePlanning;
  }

  get maintenancePlanningTime(): { start?: string; end?: string } | null {
    return this.loggerService.maintenancePlanningTime;
  }

  get maintenanceMessage(): string {
    return this.loggerService.maintenanceMessage
      || 'Strona jest obecnie aktualizowana. Spróbuj ponownie później.';
  }

  get showMaintenanceRefresh(): boolean {
    const end = this.maintenancePlanningTime?.end;
    if (!end) {
      return false;
    }
    const endTime = Date.parse(end);
    return !Number.isNaN(endTime) && Date.now() > endTime;
  }

  refreshPage(): void {
    window.location.reload();
  }

  clearData(): void {
    // Delegate to PwaService which handles clearing and reload
    //do it only when there is connection
    if (navigator.onLine) {
      this.pwaService.clearData(undefined);
    } else {
      console.warn('Cannot clear data while offline.');
      this.snackBar.open('Nie można wyczyścić danych podczas pracy offline.', 'Zamknij', {
        duration: 3000,
      });
    }
  }

  goTo(path: string) {
    this.router.navigate([path]);
  }


  goToAdmin(): void {
    // Open admin interface in new tab or same tab
    const adminUrl = `${window.location.origin}/admin/dashboard`;
    window.open(adminUrl, '_blank', 'noopener,noreferrer');
  }


  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  toggleWcagMode(): void {
    this.wcagMode = !this.wcagMode;
    if (this.wcagMode) {
      this.applyWcagMode();
    } else {
      this.removeWcagMode();
    }
    // Persist preference
    localStorage.setItem('wcagMode', this.wcagMode ? 'true' : 'false');
  }

  private applyWcagMode(): void {
    document.documentElement.classList.add('wcag-mode');
  }

  private removeWcagMode(): void {
    document.documentElement.classList.remove('wcag-mode');
  }

  ngOnInit(): void {
    localStorage.setItem('mobileApp', this.pwaService.isPwaInstalled() ? '1' : '0');

    // Check for version update and reload if necessary
    this.checkAndReloadForNewVersion();

    // Load WCAG mode preference
    this.wcagMode = localStorage.getItem('wcagMode') === 'true';
    if (this.wcagMode) {
      this.applyWcagMode();
    }

    window.addEventListener('scroll', this.onScroll, true);
    this.checkLoginStatus();
    //this.checkForUpdates();

    this.router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updatePageTitle();
        this.updateBackButtonVisibility();
        this.updateFullWidthPage();
        this.metaService.updateTag({ name: 'description', content: this.pageIntro });

        // Scroll to top on every navigation unless ScrollPositionService
        // is restoring a saved position (returning from detail → list).
        if (!this.scrollPositionService.isScrollRestoring) {
          window.scrollTo({ top: 0, behavior: 'instant' });
        }
      });

    this.userService.loggedIn.subscribe((loggedIn) => {
      this.isLoggedIn = loggedIn; // React to login state changes
      this.checkLoginStatus(); // Update admin session status as well
    });

    // Listen for offline/online events with enhanced detection
    window.addEventListener('offline', () => {
      this.wasOffline = true;
      this.snackBar.open(
        'Brak połączenia z internetem. Pracujesz w trybie offline.',
        'Zamknij',
        {
          duration: 6000,
          panelClass: ['warning-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        }
      );
      // Start polling for connectivity
      this.startConnectivityCheck();
    });

    window.addEventListener('online', () => {
      this.handleConnectionRestored();
    });

    // Initial check if already offline
    if (!navigator.onLine) {
      this.wasOffline = true;
      this.startConnectivityCheck();
    }

    this.showInstallPwa = this.pwaService.isMobileDevice();
  }

  checkLoginStatus(): void {
    // Check if the user token exists in localStorage
    this.isLoggedIn = !!localStorage.getItem('userToken');
    // Check if admin session exists
    this.hasAdminSession = !!localStorage.getItem('authToken');
  }

  async installPwa() {
    
  }

  logout(): void {
    // Remove the user tokens from localStorage
    this.userService.logoutUser();
    this.isLoggedIn = false;
    this.checkLoginStatus(); // Update admin session status

    this.userService.loggedIn.next(false); // Update the loggedIn BehaviorSubject

    this.snackBar.open('Użytkownik został wylogowany', 'Zamknij', {
      duration: 4000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center', // Center horizontally
      verticalPosition: 'bottom',
    });

    // Redirect to the home page or login page
    this.router.navigate(['/logowanie']);
  }



  private updatePageTitle(): void {
    const route = this.router.routerState.snapshot.root;
    const [title, intro] = this.getTitleFromRoute(route);
    this.pageTitle = title || '';
    this.pageIntro = intro || 'Program osłonowy ZGrane Grono powstał z myślą o wzmacnianiu lokalnej wspólnoty i budowaniu więzi między mieszkańcami Zielonej Góry. Jego głównym celem jest wspieranie różnych grup społecznych poprzez umożliwienie im korzystania z atrakcyjnych zniżek, ofert i wydarzeń, które sprzyjają aktywnemu uczestnictwu w życiu miasta'; // Update the intro text
    this.titleService.setTitle(`Zgrane Grono ${this.pageTitle.length > 1 ? '-' : ''} ${this.pageTitle}`); // Update the 
  }

  private getTitleFromRoute(route: any): [string | null, string | null] {
    let title = route.data?.['title'] || null;
    let intro = route.data?.['intro'] || null;
    if (route.firstChild) {
      [title, intro] = this.getTitleFromRoute(route.firstChild) || title;
    }
    return [title, intro];
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onScroll, true);
    // Clear connectivity check interval
    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval);
    }
  }

  private startConnectivityCheck(): void {
    // Clear any existing interval
    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval);
    }

    // Check connectivity every 3 seconds
    this.connectivityCheckInterval = setInterval(() => {
      if (navigator.onLine && this.wasOffline) {
        this.handleConnectionRestored();
      }
    }, 3000);
  }

  private async handleConnectionRestored(): Promise<void> {
    if (!this.wasOffline) {
      return;
    }

    // Stop polling
    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval);
      this.connectivityCheckInterval = null;
    }
    // Try to update service worker
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
        }
      } catch (error) {
        console.error('Error updating service worker:', error);
      }
    }

    this.snackBar.open(
      'Połączenie przywrócone.',
      '',
      {
        duration: 1500,
        panelClass: ['success-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      }
    );

    // Reset flag and hard reload to bypass cache
    this.wasOffline = false;
    setTimeout(() => {
      // Force hard reload to bypass all caches
      //window.location.href = window.location.href;
    }, 1500);
  }

  onScroll = () => {
    this.showGoTop = window.scrollY > 100;
  };

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goBack() {
    this.location.back();
  }

  private updateBackButtonVisibility(): void {
    const currentUrl = this.router.url;
    // Check if the URL has double slashes (e.g., /oferty/something, /wiadomosci/something, /partner/something)
    const urlParts = currentUrl.split('/').filter(part => part.length > 0);
    this.showGoBack = (urlParts.length > 1 || currentUrl.includes('/person-edit')) && (!currentUrl.includes('tresc'));
  }

  private updateFullWidthPage(): void {
    const currentUrl = this.router.url;
    this.fullWidthPage = currentUrl === '/zostan-naszym-partnerem' || currentUrl === '/informacje_o_karcie' || currentUrl === '/aplikacja' || currentUrl == '/nagrody'  ||  currentUrl === '/';
  }

  private checkForUpdates(): void {
    if ('serviceWorker' in navigator) {
      // Listen for new service worker installation
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Safari safeguard: prevent reload loop
        const lastControllerChange = sessionStorage.getItem('lastControllerChange');
        const now = Date.now();
        if (lastControllerChange && (now - parseInt(lastControllerChange, 10)) < 10000) {
          console.log('Controller change detected too soon, skipping reload to prevent loop');
          return;
        }
        sessionStorage.setItem('lastControllerChange', now.toString());
        window.location.reload();
      });

      // Periodically check for updates
      setInterval(() => {
        navigator.serviceWorker.getRegistration().then(registration => {
          if (registration) {
            registration.update();
          }
        });
      }, 60000); // Check every minute
    }
  }

  /**
   * Compare two version strings (e.g., "1.25.0" vs "1.27.0")
   * Returns true if version1 < version2
   */
  private isVersionLower(version1: string, version2: string): boolean {
    if (!version1 || !version2) {
      return false;
    }
    
    const v1Parts = version1.split('.').map(v => parseInt(v, 10) || 0);
    const v2Parts = version2.split('.').map(v => parseInt(v, 10) || 0);
    
    const maxLength = Math.max(v1Parts.length, v2Parts.length);
    
    for (let i = 0; i < maxLength; i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;
      
      if (v1Part < v2Part) {
        return true;
      } else if (v1Part > v2Part) {
        return false;
      }
    }
    
    return false; // versions are equal
  }

  /**
   * Check if remote version is higher than local version and trigger reload if needed.
   * Prevents endless loops by tracking the last reloaded version.
   */
  private checkAndReloadForNewVersion(): void {
    const localVersion = this.appVersion;
    const remoteVersion = this.remoteVersionNumber;
    
    // Don't proceed if versions are not available yet
    if (!localVersion || !remoteVersion) {
      return;
    }
    
    // Safari safeguard: check sessionStorage to prevent loop within same session
    const sessionReloadCheck = sessionStorage.getItem('versionReloadAttempted');
    if (sessionReloadCheck === remoteVersion) {
      console.log('Version reload already attempted in this session, skipping to prevent loop');
      return;
    }
    
    // Check if remote version is higher than local version
    if (this.isVersionLower(localVersion, remoteVersion)) {
      // Get the last version we reloaded for
      const lastReloadedVersion = localStorage.getItem('lastReloadedVersion');
      
      // Only reload if we haven't already reloaded for this remote version
      if (lastReloadedVersion !== remoteVersion) {
        console.log(`New version detected: ${remoteVersion} (current: ${localVersion}). Clearing data and reloading...`);
        
        // Mark in sessionStorage to prevent loop (Safari safeguard)
        sessionStorage.setItem('versionReloadAttempted', remoteVersion);
        
        // Pass the new version to clearData so it can preserve it through the clear
        this.pwaService.clearData(remoteVersion);
      }
    }
  }
}